"use client"

import type React from "react"
import { TrendingUp } from "lucide-react"

import { useState, useEffect, useRef } from "react"
import { Button } from "@/shared/components/ui/button"
import { Card } from "@/shared/components/ui/card"
import { Input } from "@/shared/components/ui/input"
import { Textarea } from "@/shared/components/ui/textarea"
import { Mail, Phone, MapPin, CheckCircle2, Send } from "lucide-react"

export function ContactPage() {
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [isVisible, setIsVisible] = useState(false)
  const formRef = useRef<HTMLDivElement>(null)
  const contactInfoRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setIsVisible(true)
  }, [])

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const form = e.currentTarget

    fetch("https://formsubmit.co/ajax/soporte@rapyfact.com", {
      method: "POST",
      body: new FormData(form),
    })
      .then((res) => res.ok && setIsSubmitted(true))
      .catch(() => alert("Hubo un error al enviar el mensaje. Inténtalo nuevamente."))
  }

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      <div className="absolute inset-0 bg-grid-pattern opacity-[0.02] pointer-events-none" />

      <div className="relative pt-32 pb-20 lg:pt-40 lg:pb-32">
        <div className="container mx-auto px-4 lg:px-8">
          <div
            className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-1000 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            <h1 className="text-4xl lg:text-6xl font-bold mb-6 text-foreground">Contáctenos</h1>
            <p className="text-lg lg:text-xl text-muted-foreground leading-relaxed">
              Estamos aquí para ayudarte. Envíanos un mensaje y te responderemos lo antes posible.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 max-w-7xl mx-auto">
            <div
              ref={formRef}
              className={`transition-all duration-1000 delay-200 ${
                isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"
              }`}
            >
              <Card className="p-8 lg:p-10 shadow-lg border border-border/50 bg-card/50 backdrop-blur-sm">
                {!isSubmitted ? (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    
                    <input type="hidden" name="_captcha" value="false" />
                    <input type="hidden" name="_subject" value="Nuevo mensaje desde el sitio web de Rapyfact" />
                    <input type="text" name="_honey" style={{ display: "none" }} />

                    <div className="space-y-2">
                      <label htmlFor="name" className="block text-sm font-medium text-foreground">
                        Nombre completo
                      </label>
                      <Input
                        id="name"
                        name="name"
                        placeholder="Ej. Juan Pérez"
                        required
                        className="h-12 bg-background/50 border-border/50 focus:border-primary transition-colors"
                      />
                    </div>

                    <div className="space-y-2">
                      <label htmlFor="email" className="block text-sm font-medium text-foreground">
                        Correo electrónico
                      </label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        placeholder="tuemail@ejemplo.com"
                        required
                        className="h-12 bg-background/50 border-border/50 focus:border-primary transition-colors"
                      />
                    </div>

                    <div className="space-y-2">
                      <label htmlFor="phone" className="block text-sm font-medium text-foreground">
                        Teléfono
                      </label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        placeholder="+57 300 000 0000"
                        className="h-12 bg-background/50 border-border/50 focus:border-primary transition-colors"
                      />
                    </div>

                    <div className="space-y-2">
                      <label htmlFor="message" className="block text-sm font-medium text-foreground">
                        Mensaje
                      </label>
                      <Textarea
                        id="message"
                        name="message"
                        rows={5}
                        placeholder="Escribe tu mensaje aquí..."
                        required
                        className="bg-background/50 border-border/50 focus:border-primary transition-colors resize-none"
                      />
                    </div>

                    <Button
                      type="submit"
                      className="w-full text-base font-semibold h-12 bg-primary hover:bg-primary/90 transition-all duration-300 group"
                      size="lg"
                    >
                      Enviar mensaje
                      <Send className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </form>
                ) : (
                  <div className="flex flex-col items-center justify-center text-center py-12 animate-in fade-in zoom-in duration-500">
                    <div className="w-20 h-20 rounded-full bg-green-500/10 flex items-center justify-center mb-6">
                      <CheckCircle2 className="w-12 h-12 text-green-500" />
                    </div>
                    <h2 className="text-3xl font-bold text-foreground mb-4">¡Gracias por contactarnos!</h2>
                    <p className="text-lg text-muted-foreground max-w-md leading-relaxed">
                      Tu mensaje fue enviado con éxito. Nuestro equipo de soporte se comunicará contigo pronto.
                    </p>
                    <Button className="mt-8" onClick={() => setIsSubmitted(false)} variant="outline">
                      Enviar otro mensaje
                    </Button>
                  </div>
                )}
              </Card>
            </div>/

            <div
              ref={contactInfoRef}
              className={`space-y-6 transition-all duration-1000 delay-300 ${
                isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"
              }`}
            >
              <Card className="p-6 lg:p-8 hover:shadow-lg transition-all duration-300 border border-border/50 bg-card/50 backdrop-blur-sm group">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                    <Mail className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-2 text-foreground">Correo electrónico</h3>
                    <a
                      href="mailto:soporte@rapyfact.com"
                      className="text-muted-foreground hover:text-primary transition-colors text-base"
                    >
                      soporte@rapyfact.com
                    </a>
                  </div>
                </div>
              </Card>

              <Card className="p-6 lg:p-8 hover:shadow-lg transition-all duration-300 border border-border/50 bg-card/50 backdrop-blur-sm group">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                    <Phone className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-2 text-foreground">Teléfono</h3>
                    <a
                      href="tel:+573135909091"
                      className="text-muted-foreground hover:text-primary transition-colors text-base"
                    >
                      +57 313 590 9091
                    </a>
                  </div>
                </div>
              </Card>

              <Card className="p-6 lg:p-8 hover:shadow-lg transition-all duration-300 border border-border/50 bg-card/50 backdrop-blur-sm group">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                    <MapPin className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-2 text-foreground">Dirección</h3>
                    <p className="text-muted-foreground leading-relaxed text-base">
                      Carrera 92 N° 8A - 76
                      <br />
                      Bogotá - Colombia
                    </p>
                  </div>
                </div>
              </Card>
            </div>
          </div>
          {/* CTA debajo del formulario */}
<div className="text-center mt-20 bg-gradient-to-r from-sky-50 to-white py-8 px-6 rounded-3xl shadow-md border border-gray-100 max-w-7xl mx-auto">
  <TrendingUp className="w-10 h-10 mx-auto mb-4 text-[#06A1FF]" />
  <h2 className="text-3xl font-bold mb-4 text-gray-900">
    ¿Listo para Optimizar tu Facturación?
  </h2>
  <p className="text-lg text-gray-600 mb-8">
    Únete a cientos de Profesionales que ya Confían en <span className="font-semibold text-[#06A1FF]">Rapyfact</span>.
  </p>
  <a href="https://app.rapyfact.com/register" target="_blank" rel="noopener noreferrer">
    <Button
      size="lg"
      className="bg-[#06A1FF] hover:bg-[#0590e6] text-white px-8 py-4 rounded-full shadow-md hover:scale-105 transition-all duration-300"
    >
      ¡Crear cuenta!
    </Button>
  </a>
</div>

        </div>

      </div>
    </div>
  )
}
